import { For, Match, Show, Switch, createEffect, createMemo, createSignal, on, onCleanup, untrack } from "solid-js";
import type { Me, TableView } from "../api";
import * as api from "../api";
import type { GameSession } from "../game/session";
import type { Queue } from "../game/queue";
import type { Rematches } from "../game/rematches";
import { t } from "../strings";
import { createPlayerInfos } from "../game/players";
import { Avatar } from "../ui/Avatar";
import { Flair } from "../ui/Flair";
import { rematchEndText } from "../ui/RematchBubble";
import { Hint } from "../ui/Hint";
import { GameEndDialog, GameStoppedDialog, HandEndDialog } from "../ui/Results";
import { reviewOnOpen } from "../game/reviewWanted";
import { Table } from "../ui/Table";
import { Frame, Gone } from "../ui/Frame";
import { Link } from "./Link";
import { dialogFor } from "../game/dialogs";
import { ErrorCode, PromptKind } from "../pb/mj/protocol/v1/protocol_pb";
import { CONTROLS, anyDialog, tableKey } from "../game/keys";
import { Tile } from "../ui/Tile";
import { Countdown } from "../ui/Countdown";
import { Dialog } from "../ui/Dialog";
import { SpokenLog } from "../ui/SpokenLog";
import { spokenState } from "../game/spoken";
import type { PlaySettings } from "../game/auto";
import type { View } from "../game/view";
import type { Placement } from "../game/place";
import { Aids } from "../ui/Aids";
import { keepAwake } from "../game/awake";
import { CALL_SHOWN_MS, buttonLive, isTurn } from "../game/clock";
import { limitOf } from "../game/items";
import { play } from "../game/sound";
import { PULSE, pulse } from "../game/haptics";
import type { Action } from "../pb/mj/record/v1/record_pb";
import { ActionType } from "../pb/mj/record/v1/record_pb";
import { Icon } from "../ui/Icon";

// A table by its invite link, /t/<token> (design 7 §4.1): the waiting room while open (join, leave, the
// creator's Start and Close, the link to share), the game while it plays, its result once ended.

export function TablePage(props: { token: string; me: Me; game: GameSession; go: (p: string) => void; onSettings: (s: PlaySettings) => void; queue: Queue; rematches: Rematches }) {
  const [table, setTable] = createSignal<TableView | null | undefined>(undefined);
  const [error, setError] = createSignal("");
  const [copied, setCopied] = createSignal(false);

  // Only the server saying so makes a table not found: a request that failed (a phone between networks) changes nothing, and the
  // first load tries again. It used to say "There is no such table." and stop there, a friend's waiting room gone for good.
  const load = (): Promise<void> =>
    api.getTable(props.token).then((tb) => {
      setTable(tb);
      if (error() === t.noConnection) setError(""); // it's back: the words stayed on the page
    }, (err: unknown) => {
      if (err instanceof api.ApiError && err.status !== 0) setTable(null);
      else if (table() === undefined) setTimeout(() => void load(), 2_000);
    });
  createEffect(on(() => props.token, () => void load()));
  // Poll while the table is open, while its game has not reached this tab, after the game failed to start
  // (dissolved: the table is open again), and while the game server is gone or its game broke: the game ends
  // when it is back, as the table will tell.
  const failed = () => props.game.view().error?.code === ErrorCode.TABLE_DISSOLVED;
  const broken = () => {
    const code = props.game.view().error?.code;
    return code === ErrorCode.GAME_SERVER_DOWN || code === ErrorCode.INTERNAL;
  };
  const waiting = createMemo(() => {
    const tb = table();
    return tb?.state === "open" || (tb?.state === "playing" && (props.game.view().gameId !== tb.game_id || failed() || broken()));
  });
  // Back from a lost connection, the game may have ended while it was gone.
  createEffect(
    on(
      () => props.game.status(),
      (st, was) => {
        if (st !== "open" || was === "open") return;
        const id = setTimeout(() => void load(), 1_500);
        onCleanup(() => clearTimeout(id));
      },
      { defer: true },
    ),
  );
  createEffect(() => {
    if (!waiting()) return;
    const id = setInterval(load, 2_000);
    onCleanup(() => clearInterval(id));
  });

  const action = (a: "join" | "leave" | "start" | "close") => {
    setError("");
    api.tableAction(props.token, a).then(
      (tb) => (a === "leave" || a === "close" ? props.go("/") : setTable(tb)),
      (err: unknown) => {
        if (err instanceof api.ApiError && typeof err.body.token === "string") setError(`${t.alreadyAtTable}`);
        else setError(err instanceof Error ? err.message : String(err));
        void load();
      },
    );
  };

  // In the game: the table is the tab's, its game reached this tab and did not fail to start.
  const inGame = createMemo(() => {
    const tb = table();
    const v = props.game.view();
    const dissolved = v.error?.code === ErrorCode.TABLE_DISSOLVED && v.gameId === tb?.game_id;
    return !!tb && tb.seated && !!tb.game_id && v.gameId === tb.game_id && !dissolved;
  });

  return (
    <Switch>
      <Match when={table() === undefined}>
        <Frame>
          <p class="loading">{t.loading}</p>
        </Frame>
      </Match>
      <Match when={table() === null}>
        <Gone text={t.noSuchTable} go={props.go} />
      </Match>
      <Match when={inGame()}>
        <GameView game={props.game} go={props.go} token={props.token} gameId={table()!.game_id!} kind={table()!.kind} length={table()!.length} ended={table()!.state === "ended"} endReason={table()!.end_reason} me={props.me.public_id} queue={props.queue} rematches={props.rematches} guest={props.me.guest} settings={props.me.settings} onSettings={props.onSettings} />
      </Match>
      <Match when={table()}>
        {(tb) => {
          const creator = () => tb().creator === props.me.public_id;
          const link = () => `${location.origin}/t/${tb().token}`;
          return (
            <Frame title={t.table} class="waiting-room">
              <p>
                {tb().rules === "wrc-2025" ? t.wrc : t.tenhou} · {tb().length === "east" ? t.east : t.eastSouth} · {t.clocksShort[tb().clock ?? "standard"]}
              </p>
              <Show when={failed() && tb().state === "open"}>
                <p class="notice" role="alert">{tb().seats.some((p) => p.away) ? t.gameDissolvedAway(tb().seats.filter((p) => p.away).map((p) => p.display_name).join(", ")) : t.gameDissolved}</p>
              </Show>
              <Show when={tb().state === "open"}>
                <ul class="seats">
                  <For each={[0, 1, 2, 3]}>
                    {(i) => (
                      <li>
                        <Show when={tb().seats[i]} fallback={<span class="small">{t.botWillPlay}</span>}>
                          {(p) => (
                            <>
                              <Avatar id={p().avatar} /> {p().display_name}{" "}
                              <Show when={p().public_id === tb().creator}>
                                <span class="tag">{t.host}</span>
                              </Show>
                              <Show when={p().away}>
                                <span class="tag warn">{t.away}</span>
                                <Show when={creator()}>
                                  <button type="button" class="link" onClick={() => void api.removeSeat(tb().token, p().public_id).then(setTable, (err: unknown) => setError(err instanceof Error ? err.message : String(err)))}>
                                    {t.seatToBot}
                                  </button>
                                </Show>
                              </Show>
                            </>
                          )}
                        </Show>
                      </li>
                    )}
                  </For>
                </ul>
                <div class="row">
                  <input readOnly value={link()} aria-label={t.inviteLink} onFocus={(e) => e.currentTarget.select()} />
                  <button type="button" class="secondary" onClick={() => navigator.clipboard?.writeText(link()).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2_000);
                  }, () => setError(t.copyFailed))}>
                    {copied() ? t.copied : t.copyLink}
                  </button>
                </div>
                <p class="small">{tb().seats.length < 4 || tb().seated ? t.shareLink : t.tableFull}</p>
                <Show when={!tb().seated && tb().seats.length >= 4}>
                  <StartOwn go={props.go} />
                </Show>
                {/* While they wait, the basics: joining by a friend's link skipped the question and the lessons (review 2026-11-01). */}
                <Show when={tb().seated && props.me.guest && !props.me.settings.experience}>
                  <p class="small">
                    <Link go={props.go} to="/learn">
                      {t.learnWhileWaiting}
                    </Link>
                  </p>
                </Show>
                <Show when={error()}>
                  <p class="notice" role="alert">{error()}</p>
                </Show>
                <div class="row">
                  <Show when={!tb().seated && tb().seats.length < 4}>
                    <button type="button" onClick={() => action("join")}>
                      {t.join}
                    </button>
                  </Show>
                  <Show when={creator()}>
                    <button type="button" onClick={() => action("start")}>
                      {t.startGame}
                    </button>
                  </Show>
                  <Show when={tb().seated && !creator()}>
                    <button type="button" class="secondary" onClick={() => action("leave")}>
                      {t.leave}
                    </button>
                  </Show>
                  <Show when={creator()}>
                    <button type="button" class="secondary" onClick={() => action("close")}>
                      {t.closeTable}
                    </button>
                  </Show>
                </div>
                <Show when={!creator() && tb().seated}>
                  <p class="small">{t.waitingForHost}</p>
                </Show>
              </Show>
              <Show when={tb().state === "playing"}>
                <p>{tb().seated ? t.connecting : t.inProgress}</p>
                <Show when={tb().seated} fallback={<StartOwn go={props.go} />}>
                  <SlowToConnect go={props.go} />
                </Show>
              </Show>
              <Show when={tb().state === "ended"}>
                <p>
                  {tb().end_reason === "aborted" ? t.tableStopped : t.tableEnded}{" "}
                  <Show when={tb().game_id && tb().end_reason !== "aborted"}>
                    <Link go={props.go} to={`/g/${tb().game_id}`}>
                      {tb().seated ? t.reviewThisGame : t.watchReplay}
                    </Link>
                  </Show>
                </p>
                <Show when={tb().seated && !props.me.guest}>
                  <EndedTable token={props.token} kind={tb().kind} stopped={tb().end_reason === "aborted"} rematches={props.rematches} go={props.go} />
                </Show>
              </Show>
              <Show when={tb().state === "closed"}>
                <p>{tb().kind === "private" ? t.tableClosed : t.tableDidNotStart}</p>
                <StartOwn go={props.go} />
              </Show>
            </Frame>
          );
        }}
      </Match>
    </Switch>
  );
}

/** A dead end's way on. */
function StartOwn(props: { go: (p: string) => void }) {
  return (
    <button type="button" onClick={() => props.go("/")}>
      {t.startYourOwn}
    </button>
  );
}

/** Seated at a table in play whose game has not reached this tab. It should take a moment: if not, there is the way out. */
function SlowToConnect(props: { go: (p: string) => void }) {
  const [slow, setSlow] = createSignal(false);
  const id = setTimeout(() => setSlow(true), 10_000);
  onCleanup(() => clearTimeout(id));
  return (
    <Show when={slow()}>
      <p class="notice" role="alert">{t.connectingSlow}</p>
      <button type="button" class="secondary" onClick={() => props.go("/")}>
        {t.home}
      </button>
    </Show>
  );
}

/** An ended table reached from a bubble, with its game's end no longer on this tab: who is there, and the vote. A game the server
 *  stopped has no places: the seats would all say first. */
function EndedTable(props: { token: string; kind: TableView["kind"]; stopped: boolean; rematches: Rematches; go: (p: string) => void }) {
  createEffect(() => {
    props.rematches.at(props.token);
    onCleanup(() => props.rematches.at(null));
  });
  const p = () => props.rematches.of(props.token);
  const open = () => (p() && !p()!.ended ? p() : undefined);
  const [error, setError] = createSignal("");
  const rematch = () => {
    setError("");
    props.rematches.accept(props.token).then(
      (tb) => {
        if (tb.token !== props.token) props.go(`/t/${tb.token}`);
      },
      (err: unknown) => {
        if (!(err instanceof api.ApiError && err.status === 409)) setError(err instanceof Error ? err.message : String(err));
      },
    );
  };
  createEffect(() => {
    const next = p()?.next;
    if (next) props.go(`/t/${next}`);
  });
  return (
    <section class="ended-table">
      <ol class="history-list">
        <For each={[...(props.rematches.end()?.seats ?? [])].sort((a, b) => a.placement - b.placement)}>
          {(s) => (
            <li class="ended-seat">
              <Show when={!props.stopped}>
                <span>{t.place[s.placement - 1]}</span>
              </Show>
              <span class="who">
                <Avatar id={s.avatar} /> {s.name} <Flair f={s.flair} />
              </span>
              <Show when={s.presence}>
                <span class={`end-presence ${s.presence}`}>
                  {t.presenceEnd[s.presence!]}
                  <Show when={open()?.votes.find((v) => v.public_id === s.public_id)}>
                    {(a) => <> · {a().accepted ? t.rematchAccepted : t.rematchPending}</>}
                  </Show>
                </span>
              </Show>
            </li>
          )}
        </For>
      </ol>
      <Show when={error()}>
        <p class="notice" role="alert">{error()}</p>
      </Show>
      <Show when={p()?.ended && p()!.ended !== "started"}>
        <p class="notice" role="alert">{rematchEndText(p()!)}</p>
      </Show>
      <Show when={props.kind !== "placement"}>
        <div class="row">
          <Show when={open()}>
            <span>{open()!.accepted ? t.rematchWaiting(open()!.ready) : t.rematchAsked(open()!.proposer_name, open()!.ready)}</span>
          </Show>
          <Show when={!open()?.accepted}>
            <button type="button" onClick={rematch}>
              {open() ? t.accept : t.rematch}
            </button>
          </Show>
          <Show when={open()}>
            <button type="button" class="secondary" onClick={() => props.rematches.decline(props.token)}>
              {open()!.accepted ? t.cancel : t.decline}
            </button>
          </Show>
        </div>
      </Show>
    </section>
  );
}

const READ_MS = 15_000;

/** How long a hand's end stays on the table before its result. */
function holdFor(v: View): number {
  const limits = (v.result?.wins ?? []).map((w) => limitOf(w, v.preset).limit);
  if (limits.some((l) => l === "yakuman" || l === "kazoe" || l === "yonbaiman")) return 3000;
  return limits.some(Boolean) ? 2200 : 1600;
}

function GameView(props: {
  guest: boolean;
  ended: boolean; // the table says its game is over
  endReason?: string;
  kind: TableView["kind"];
  length: TableView["length"];
  me: string;
  queue: Queue;
  rematches: Rematches;
  game: GameSession;
  go: (p: string) => void;
  token: string;
  gameId: string;
  settings: PlaySettings;
  onSettings: (s: PlaySettings) => void;
}) {
  const view = () => props.game.view();
  const infos = createPlayerInfos(() => view().players.map((p) => p.id));
  // Ranked tables have no aids (design 8 P4), the placement game among them (design 14 §6.4).
  createEffect(() => props.game.setAids(props.kind === "private"));
  onCleanup(() => props.game.setAids(false));
  keepAwake(() => !props.ended && !view().gameEnd);
  // In another tab, the tab's own name is what can be seen (review 2026-11-01: no sign at all, and the server played the seat). A turn's, not an offer's: at a
  // table without assists an offer's name would tell what the hand can do.
  const yourTurn = createMemo(() => {
    const p = view().prompt;
    return !!p && !p.sent && isTurn(p.prompt.kind);
  });
  createEffect(() => {
    document.title = yourTurn() ? t.yourTurnTitle : `${t.tableTitle} · ${t.siteTitle}`;
  });
  // Without assists the call buttons are live for a fixed time from each tile another seat offers, a discard or a tile added to a kan: then the server moves on.
  const offered = createMemo(() => {
    const v = view();
    const d = v.lastDiscard;
    const c = v.callout;
    const kan = c && c.seat !== v.seat && (c.action === "ADDED_KAN" || c.action === "CLOSED_KAN") ? `k${c.seq}` : "";
    return kan || (d && d.seat !== v.seat ? `${v.hand?.index}:${d.seat}:${d.index}` : "");
  });
  const [callsOpen, setCallsOpen] = createSignal(false);
  createEffect(
    on(offered, (o) => {
      setCallsOpen(!!o);
      if (!o) return;
      const id = setTimeout(() => setCallsOpen(false), CALL_SHOWN_MS);
      onCleanup(() => clearTimeout(id));
    }),
  );
  const [keysShown, setKeysShown] = createSignal(false);
  const [said, setSaid] = createSignal("");
  // A press with nothing behind it is ruled on when the window closes (the ruling queues behind it): dark at once, or the silence invites a second press and its fine.
  const pressEmpty = (type: ActionType) => {
    setCallsOpen(false);
    props.game.press(type);
  };
  createShortcuts(
    view,
    (a) => props.game.act(a),
    () => props.kind !== "private",
    callsOpen,
    anyDialog,
    () => setKeysShown(true),
    () => setSaid(spokenState(view())),
  );
  const [hidden, setHidden] = createSignal<number | null>(null); // the hand whose result was closed
  // Once a connection was lost, every retry says so until one is open: the banner used to alternate between
  // "Connection lost" and "Connecting…" with each attempt (audit 2026-10-14).
  const [lost, setLost] = createSignal(false);
  createEffect(() => {
    const st = props.game.status();
    if (st === "open") setLost(false);
    else if (st === "closed") setLost(true);
  });
  // A hand's result waits a moment after the hand ends, so the hands are seen laid down (design 10 §7).
  const ended = createMemo(() => (view().result?.end ? (view().hand?.index ?? -1) : null));
  const [settled, setSettled] = createSignal<number | null>(null);
  // Come back to a table already over, by Back from its review: straight to its end. It played the last hand's end out again, stale scores and all.
  const overOnArrival = untrack(() => props.ended);
  createEffect(() => {
    const e = ended();
    if (e === null) return;
    if (overOnArrival) {
      setHidden(e);
      setSettled(e);
      return;
    }
    // The bigger the hand, the longer it is looked at. A hand that ended the game too: it used to go straight to the sheet.
    const id = setTimeout(() => setSettled(e), holdFor(untrack(view)));
    onCleanup(() => clearTimeout(id));
  });
  const dialog = () => (ended() !== null && settled() !== ended() ? null : dialogFor(view(), hidden()));
  // The table says nothing of what the hand paid until its result has: the sheet is the reveal, and the scores move as it closes.
  const reveal = () => ended() !== null && hidden() !== ended();
  // How long the result stays with other people waiting: the server's limit, from when the hand ended (mjcore/server/table.h). Alone, as long as it is read.
  const [endedAt, setEndedAt] = createSignal(0);
  createEffect(
    on(ended, (e) => {
      if (e !== null) setEndedAt(performance.now());
    }),
  );
  const readBy = () => (view().players.filter((p) => !p.bot).length > 1 && !view().gameEnd ? endedAt() + READ_MS : null);
  // The sheet closed with others still reading: the table sat there for up to 12 s, with nothing saying why (UX review 2026-11-02).
  const waitingNext = () => (ended() !== null && hidden() === ended() && !stopped() ? readBy() : null);
  // The end screen (design 14 §8): while it shows, the server is told this player is on it, and tells the ratings, who
  // else is, and the rematch's vote.
  const [rematchError, setRematchError] = createSignal("");
  createEffect(() => {
    if (dialog() !== "game" || props.guest) return;
    props.rematches.at(props.token);
    onCleanup(() => props.rematches.at(null));
  });
  const rematch = () => {
    setRematchError("");
    props.rematches.accept(props.token).then(
      (tb) => {
        if (tb.token !== props.token) props.go(`/t/${tb.token}`);
      },
      (err: unknown) => {
        // A failed start is told by the proposal itself.
        if (!(err instanceof api.ApiError && err.status === 409)) setRematchError(err instanceof Error ? err.message : String(err));
      },
    );
  };
  // Everyone is taken to a started rematch from the end screen; the game connection does it too, this is for a tab that
  // heard the proposal first.
  createEffect(() => {
    const p = props.rematches.of(props.token);
    if (p?.next && dialog() === "game") props.go(`/t/${p.next}`);
  });
  // The table waits for the result to be read (owner, 2026-10-14): the server is told when it shows and when
  // it is closed, and starts the next hand when every seat reading it is done (at most 15 s after the end).
  createEffect(
    on(settled, (e) => {
      if (e !== null && e === untrack(ended) && !untrack(view).gameEnd) props.game.reading(e, false);
    }),
  );
  const closeResult = () => {
    const e = view().hand?.index ?? -1;
    setHidden(e);
    if (e >= 0) props.game.reading(e, true);
  };
  // Tactile feedback (design 10 §8): a discard's click as it lands (softer for other seats), a pulse as the player's own lands.
  const onLand = (p: Placement) => {
    if (p.where !== "river") return;
    const s = props.settings;
    const own = p.seat === view().seat;
    if (s.sound) play(own ? "discard" : "discardFar", s.volume);
    if (own && s.haptics) pulse(PULSE.land);
  };
  const status = () => props.game.status();
  // Its game over, and no end of it here: the server stopped (design 4 §6.4), or this tab was away when it ended. Nothing more will come.
  const stopped = () => props.ended && !view().gameEnd;
  // One message at a time, the one that matters most: they all stand in the same place, and used to lie on top of each other.
  // The server's own words are for the log: "signed in elsewhere" meant nothing here.
  const [slowShuffle, setSlowShuffle] = createSignal(false);
  const shuffling = createMemo(() => view().phase === "shuffling");
  createEffect(() => {
    if (!shuffling()) return setSlowShuffle(false);
    const id = setTimeout(() => setSlowShuffle(true), 5_000);
    onCleanup(() => clearTimeout(id));
  });
  const banner = (): { text: string; warn?: boolean } | null => {
    if (stopped() || status() === "signed-out") return null;
    if (status() !== "open") return { text: lost() ? t.reconnecting : t.connecting, warn: lost() };
    const code = view().error?.code;
    if (code === ErrorCode.GAME_SERVER_DOWN) return { text: t.gameServerDown, warn: true };
    if (code !== undefined && code !== ErrorCode.TABLE_DISSOLVED && code !== ErrorCode.SUPERSEDED && code !== ErrorCode.NOT_AUTHENTICATED) return { text: t.serverError, warn: true };
    if (shuffling()) return { text: slowShuffle() ? t.shufflingSlow : t.shuffling };
    if (!view().hand && view().phase === "waiting") return { text: t.waiting };
    return null;
  };
  // The seat played for it (design 4 §5.2): said plainly while it lasts, and what went, as it comes back. A small tag was the only sign, and a player
  // back from a distraction lost tiles with nothing to explain it (UX review 2026-11-02).
  const onAuto = () => !!view().connections[view().seat]?.auto && !view().gameEnd;
  const [forYouShown, setForYouShown] = createSignal(false);
  createEffect(
    on(
      () => view().forYou.length,
      (n) => {
        setForYouShown(n > 0);
        if (n === 0) return;
        const id = setTimeout(() => setForYouShown(false), 12_000);
        onCleanup(() => clearTimeout(id));
      },
    ),
  );
  // A game against bots is its one player's to end (design 14 §3); a table with other people plays on without them.
  const againstBots = () => props.kind !== "rated" && view().players.length > 0 && view().players.filter((p) => !p.bot).length === 1;
  const [leaving, setLeaving] = createSignal(false);
  const [leaveError, setLeaveError] = createSignal("");
  const leave = () => {
    setLeaveError("");
    api.tableAction(props.token, "abandon").then(
      () => props.go("/"),
      (err: unknown) => setLeaveError(err instanceof Error ? err.message : String(err)),
    );
  };
  return (
    <main class="play">
      <h1 class="sr-only">{t.tableTitle}</h1>
      {/* Leaving mid-game is safe: the game goes on (the server answers for an absent player), and the lobby
          offers to resume it. */}
      <nav class="play-nav">
        <button type="button" class="secondary" onClick={() => props.go("/")}>
          <Icon name="home" /> {t.home}
        </button>
        <Show when={againstBots() && !view().gameEnd}>
          <button type="button" class="secondary" onClick={() => setLeaving(true)}>
            {t.leaveGame}
          </button>
        </Show>
        <Show when={props.kind !== "private"}>
          <span class="table-kind" title={props.kind === "placement" ? t.assessmentTableHint : t.rankedTableHint}>
            {props.kind === "placement" ? t.assessmentTable : t.rankedTable}
          </span>
        </Show>
      </nav>
      <SpokenLog view={view} assisted={props.kind === "private"} said={said()} />
      <Show when={keysShown()}>
        <Dialog label={t.keysTitle} onClose={() => setKeysShown(false)}>
            <h2>{t.keysTitle}</h2>
            <ul class="keys-list">
              <For each={props.kind === "private" ? t.keys : t.keysNoAssist}>{(k) => <li>{k}</li>}</For>
            </ul>
            <button type="button" onClick={() => setKeysShown(false)}>
              {t.hints.close}
            </button>
        </Dialog>
      </Show>
      <Show when={leaving()}>
        <Dialog label={t.leaveGame} alert onClose={() => setLeaving(false)}>
            <h2>{t.leaveGameTitle}</h2>
            <p>{props.kind === "placement" ? t.leaveAssessmentText : t.leaveGameText}</p>
            <Show when={leaveError()}>
              <p class="notice" role="alert">{leaveError()}</p>
            </Show>
            <div class="row">
              <button type="button" data-first onClick={() => setLeaving(false)}>
                {t.keepPlaying}
              </button>
              <button type="button" class="danger" onClick={leave}>
                {t.leaveGame}
              </button>
            </div>
        </Dialog>
      </Show>
      <Show
        when={status() === "superseded"}
        fallback={
          <Show
            when={banner()}
            fallback={
              <Show when={onAuto() || forYouShown()}>
                <div class="banner for-you" role="status">
                  <Show when={onAuto()} fallback={<span>{t.forYouAway}</span>}>
                    <span class="warn">{t.forYouAuto}</span>
                  </Show>
                  <Show when={view().forYou.length > 0}>
                    <span class="for-you-tiles">
                      <Show when={onAuto()}>
                        <span>{t.forYouPlayed}</span>
                      </Show>
                      <For each={view().forYou.slice(-6)}>{(id) => <Tile id={id} red={view().redFives} size="s" />}</For>
                    </span>
                  </Show>
                </div>
              </Show>
            }
          >
            {(b) => <div class={`banner${b().warn ? " warn" : ""}`} role="status">{b().text}</div>}
          </Show>
        }
      >
        <div class="banner">
          {t.superseded}{" "}
          <button type="button" class="link" onClick={() => props.game.resume()}>
            {t.playHere}
          </button>
        </div>
      </Show>
      <Show when={waitingNext()}>
        {(by) => (
          <div class="banner" role="status">
            <Countdown class="" text={(now) => t.nextHandIn(Math.max(0, Math.ceil((by() - now) / 1000)))} />
          </div>
        )}
      </Show>
      <Table
        v={view()}
        onAct={(a) => props.game.act(a)}
        hand={props.game.hand()}
        onRearrange={props.settings.auto_sort ? undefined : (tile, to) => props.game.rearrange(tile, to)}
        feel={props.settings.feel}
        onLand={onLand}
        heldScores={reveal()}
        view={props.settings.view}
        angle={props.settings.angle}
        info={infos()}
        ratings={props.kind === "rated"}
        noAssists={props.kind !== "private"}
        onPress={pressEmpty}
        callsOpen={props.kind !== "private" ? callsOpen() : undefined}
        offline={status() !== "open"}
        keysOff={anyDialog}
      />
      <Aids settings={props.settings} onSettings={props.onSettings} hidden={props.kind !== "private"} />
      <Show when={stopped()}>
        <GameStoppedDialog aborted={props.endReason === "aborted"} onHome={() => props.go("/")} onReplay={() => props.go(`/g/${props.gameId}`)} />
      </Show>
      <Show when={!stopped() && dialog() === "hand" && view().result}>
        {(r) => <HandEndDialog v={view()} r={r()} onClose={closeResult} readBy={readBy()} />}
      </Show>
      <Show when={props.kind === "private"}>
        <Hint v={view()} settings={props.settings} onSettings={props.onSettings} result={dialog() === "hand" && !!view().result} />
      </Show>
      <Show when={dialog() === "game" && view().gameEnd}>
        {(end) => (
          <GameEndDialog
            v={view()}
            end={end()}
            onHome={() => props.go("/")}
            guest={props.guest}
            onReplay={() => props.go(`/g/${props.gameId}`)}
            onReview={() => {
              reviewOnOpen(props.gameId);
              props.go(`/g/${props.gameId}`);
            }}
            rematch={{
              label: view().players.filter((p) => !p.bot).length === 1 ? t.playAgain : t.rematch,
              error: rematchError(),
              go: rematch,
            }}
            state={props.rematches.end()}
            proposal={props.rematches.of(props.token)}
            onDecline={() => props.rematches.decline(props.token)}
            me={props.me}
            onRankedAgain={
              props.kind === "rated"
                ? () => {
                    props.queue.setLength(props.length);
                    void props.queue.join(props.length).then(() => props.go("/"));
                  }
                : props.kind === "placement" && props.rematches.end()?.settled
                  ? () => void props.queue.join(props.queue.length()).then(() => props.go("/"))
                  : undefined
            }
          />
        )}
      </Show>
    </main>
  );
}

// Desktop keys (design 8 item 3): Space or Escape passes; T tsumo, R ron (or riichi), P pon, C chi, K kan;
// Enter discards the drawn tile. Only what the open prompt offers; never while typing in a field.
const KEYS: Record<string, ActionType[]> = {
  " ": [ActionType.PASS],
  escape: [ActionType.PASS],
  t: [ActionType.TSUMO],
  r: [ActionType.RON, ActionType.DECLARE_RIICHI],
  p: [ActionType.PON],
  c: [ActionType.CHI],
  k: [ActionType.CLOSED_KAN, ActionType.ADDED_KAN, ActionType.OPEN_KAN],
};

function createShortcuts(view: () => View, act: (a: Action) => void, noAssists: () => boolean, callsOpen: () => boolean, dialogOpen: () => boolean, showKeys: () => void, speak: () => void) {
  const onKey = (e: KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.ctrlKey || e.metaKey || e.altKey) return;
    const key = e.key.toLowerCase();
    if (!dialogOpen() && key === "?") return showKeys();
    if (!dialogOpen() && key === "l") return speak();
    if (!tableKey(key, e.target instanceof Element && !!e.target.closest(CONTROLS), dialogOpen())) return;
    const v = view();
    const p = v.prompt;
    if (noAssists() && KEYS[key]) {
      // A key that costs a hand when empty is held, as a finger holds its button: OpenControls has those. Passing costs nothing, and says nothing either way.
      if (!KEYS[key]!.includes(ActionType.PASS)) return;
      if (!buttonLive(KEYS[key]!, p?.prompt.kind === PromptKind.AFTER_DRAW, callsOpen())) return;
      if (!p?.prompt.legal.some((x) => x.type === ActionType.PASS)) {
        e.preventDefault();
        return;
      }
    }
    if (!p || p.sent) return;
    if (key === "c" && p.prompt.legal.filter((x) => x.type === ActionType.CHI).length > 1) return; // the buttons ask which
    let a: Action | undefined;
    if (key === "enter") {
      const drawn = v.seats[v.seat]?.drawn;
      a = p.prompt.legal.find((x) => x.type === ActionType.DISCARD && x.tiles[0] === drawn);
    } else {
      for (const type of KEYS[key] ?? []) a ??= p.prompt.legal.find((x) => x.type === type);
    }
    if (a) {
      e.preventDefault();
      act(a);
    }
  };
  addEventListener("keydown", onKey);
  onCleanup(() => removeEventListener("keydown", onKey));
}
